import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Power,
  PowerOff,
  Star,
  StarOff,
  TestTube,
  Eye,
  EyeOff,
  Settings,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Copy,
  Save,
  X
} from 'lucide-react';
import api from '../../utils/api';

interface PaymentGateway {
  _id: string;
  name: string;
  displayName: string;
  type: 'RAZORPAY';
  provider: 'RAZORPAY';
  environment: 'SANDBOX' | 'PRODUCTION';
  isActive: boolean;
  isDefault: boolean;
  status: 'ACTIVE' | 'INACTIVE' | 'MAINTENANCE' | 'DEPRECATED';
  configuration: {
    keyId: string; // Razorpay Key ID
    keySecret: string; // Razorpay Key Secret
    webhookSecret?: string;
    merchantName?: string;
    companyName?: string;
    companyLogo?: string;
    themeColor?: string;
    callbackUrl?: string;
  };
  supportedMethods: string[];
  fees: {
    percentage: number;
    fixedAmount: number;
    minAmount: number;
    maxAmount: number;
  };
  limits: {
    minTransaction: number;
    maxTransaction: number;
    dailyLimit: number;
  };
  metadata: {
    description?: string;
    processingTime?: string;
  };
  lastTested?: string;
  testResults?: {
    success: boolean;
    message: string;
    testedAt: string;
  };
  createdAt: string;
  updatedAt: string;
}

const PaymentGatewayManagement: React.FC = () => {
  const [gateways, setGateways] = useState<PaymentGateway[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingGateway, setEditingGateway] = useState<PaymentGateway | null>(null);
  const [showSensitiveData, setShowSensitiveData] = useState<{ [key: string]: boolean }>({});
  const [testingGateway, setTestingGateway] = useState<string | null>(null);
  const [filter, setFilter] = useState({
    type: '',
    provider: '',
    environment: '',
    status: ''
  });

  const [formData, setFormData] = useState<{
    name: string;
    displayName: string;
    type: PaymentGateway['type'];
    provider: PaymentGateway['provider'];
    environment: PaymentGateway['environment'];
    configuration: {
      keyId: string;
      keySecret: string;
      webhookSecret?: string;
      merchantName?: string;
      companyName?: string;
      companyLogo?: string;
      themeColor?: string;
      callbackUrl?: string;
    };
    supportedMethods: string[];
    fees: {
      percentage: number;
      fixedAmount: number;
      minAmount: number;
      maxAmount: number;
    };
    limits: {
      minTransaction: number;
      maxTransaction: number;
      dailyLimit: number;
    };
    metadata: {
      description?: string;
      processingTime?: string;
    };
  }>({
    name: 'razorpay-gateway',
    displayName: 'Razorpay Payment Gateway',
    type: 'RAZORPAY',
    provider: 'RAZORPAY',
    environment: 'SANDBOX',
    configuration: {
      keyId: '',
      keySecret: '',
      webhookSecret: '',
      merchantName: 'E-Nest India',
      companyName: 'E-Nest India',
      companyLogo: '',
      themeColor: '#3B82F6',
      callbackUrl: ''
    },
    supportedMethods: ['card', 'upi', 'wallet', 'netbanking'],
    fees: {
      percentage: 2.4, // Razorpay standard rate
      fixedAmount: 0,
      minAmount: 0,
      maxAmount: 0
    },
    limits: {
      minTransaction: 1,
      maxTransaction: 1500000, // 15 Lakh max for Razorpay
      dailyLimit: 5000000 // 50 Lakh daily limit
    },
    metadata: {
      description: 'Razorpay payment gateway for secure online payments',
      processingTime: 'Instant'
    }
  });

  useEffect(() => {
    fetchGateways();
  }, [filter]);

  const fetchGateways = async () => {
    try {
      setIsLoading(true);
      const queryParams = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });

      const response = await api.get(`/payment-gateways?${queryParams}`);
      setGateways(response.data.gateways);
    } catch (error) {
      console.error('Error fetching gateways:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingGateway) {
        await api.put(`/payment-gateways/${editingGateway._id}`, formData);
      } else {
        await api.post('/payment-gateways', formData);
      }
      
      setShowModal(false);
      setEditingGateway(null);
      resetForm();
      fetchGateways();
    } catch (error: any) {
      console.error('Error saving gateway:', error);
      alert(error.response?.data?.message || 'Failed to save gateway');
    }
  };

  const resetForm = () => {
    setFormData({
      name: 'razorpay-gateway',
      displayName: 'Razorpay Payment Gateway',
      type: 'RAZORPAY',
      provider: 'RAZORPAY',
      environment: 'SANDBOX',
      configuration: {
        keyId: '',
        keySecret: '',
        webhookSecret: '',
        merchantName: 'E-Nest India',
        companyName: 'E-Nest India',
        companyLogo: '',
        themeColor: '#3B82F6',
        callbackUrl: ''
      },
      supportedMethods: ['card', 'upi', 'wallet', 'netbanking'],
      fees: {
        percentage: 2.4,
        fixedAmount: 0,
        minAmount: 0,
        maxAmount: 0
      },
      limits: {
        minTransaction: 1,
        maxTransaction: 1500000,
        dailyLimit: 5000000
      },
      metadata: {
        description: 'Razorpay payment gateway for secure online payments',
        processingTime: 'Instant'
      }
    });
  };

  const handleEdit = (gateway: PaymentGateway) => {
    setEditingGateway(gateway);
    setFormData({
      name: gateway.name,
      displayName: gateway.displayName,
      type: gateway.type,
      provider: gateway.provider,
      environment: gateway.environment,
      configuration: { ...gateway.configuration },
      supportedMethods: gateway.supportedMethods,
      fees: gateway.fees,
      limits: gateway.limits,
      metadata: gateway.metadata
    });
    setShowModal(true);
  };

  const toggleGatewayStatus = async (gatewayId: string) => {
    try {
      const response = await api.patch(`/payment-gateways/${gatewayId}/toggle-status`);
      console.log('Toggle status response:', response.data);
      fetchGateways();
      alert(response.data.message || 'Status updated successfully');
    } catch (error: any) {
      console.error('Error toggling gateway status:', error);
      alert(`Error: ${error.response?.data?.message || error.message || 'Failed to toggle status'}`);
    }
  };

  const setAsDefault = async (gatewayId: string) => {
    try {
      const response = await api.patch(`/payment-gateways/${gatewayId}/set-default`);
      console.log('Set default response:', response.data);
      fetchGateways();
      alert(response.data.message || 'Default gateway updated successfully');
    } catch (error: any) {
      console.error('Error setting default gateway:', error);
      alert(`Error: ${error.response?.data?.message || error.message || 'Failed to set as default'}`);
    }
  };

  const testGateway = async (gatewayId: string) => {
    try {
      setTestingGateway(gatewayId);
      const response = await api.post(`/payment-gateways/${gatewayId}/test`);
      alert(`Test Result: ${response.data.testResult.message}`);
      fetchGateways();
    } catch (error: any) {
      alert(`Test Failed: ${error.response?.data?.message || 'Unknown error'}`);
    } finally {
      setTestingGateway(null);
    }
  };

  const deleteGateway = async (gatewayId: string, gatewayName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${gatewayName}"?`)) return;
    
    try {
      await api.delete(`/payment-gateways/${gatewayId}`);
      fetchGateways();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to delete gateway');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const toggleSensitiveData = (gatewayId: string) => {
    setShowSensitiveData(prev => ({
      ...prev,
      [gatewayId]: !prev[gatewayId]
    }));
  };

  const getStatusIcon = (status: string, isActive: boolean) => {
    if (!isActive) return <PowerOff className="h-4 w-4 text-gray-400" />;
    
    switch (status) {
      case 'ACTIVE':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'MAINTENANCE':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'DEPRECATED':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <XCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getEnvironmentBadge = (environment: string) => {
    const isProduction = environment === 'PRODUCTION';
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${
        isProduction 
          ? 'bg-green-100 text-green-800' 
          : 'bg-blue-100 text-blue-800'
      }`}>
        {environment}
      </span>
    );
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
            Payment Gateway Management
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            Configure and manage payment gateways for your store
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="w-full sm:w-auto bg-primary-600 hover:bg-primary-700 text-white px-4 py-3 sm:py-2 rounded-xl sm:rounded-lg flex items-center justify-center space-x-2 text-base sm:text-sm font-semibold shadow-lg transition-all active:transform active:scale-[0.98] min-h-[48px]"
        >
          <Plus className="h-5 w-5 sm:h-4 sm:w-4" />
          <span>Add Gateway</span>
        </button>
      </div>

      {/* Status Filter */}
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <div className="flex space-x-4">
          <select
            value={filter.status}
            onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value }))}
            className="px-3 py-3 sm:py-2 border border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-800 text-gray-900 dark:text-gray-100 rounded-lg sm:rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px] text-base sm:text-sm w-full sm:w-auto"
          >
            <option value="">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
            <option value="MAINTENANCE">Maintenance</option>
            <option value="DEPRECATED">Deprecated</option>
          </select>
        </div>
        
        <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 text-center sm:text-right">
          Only Razorpay gateway configurations are supported
        </div>
      </div>

      {/* Gateway List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading payment gateways...</p>
          </div>
        ) : gateways.length === 0 ? (
          <div className="text-center py-8">
            <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No payment gateways configured</p>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="mt-4 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all active:transform active:scale-[0.98] min-h-[48px]"
            >
              Add your first payment gateway
            </button>
          </div>
        ) : (
          gateways.map((gateway) => (
            <div
              key={gateway._id}
              className="bg-white dark:bg-secondary-800 rounded-xl shadow-md border border-gray-200 dark:border-secondary-700 p-4 sm:p-6"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {gateway.displayName}
                    </h3>
                    {gateway.isDefault && (
                      <Star className="h-4 w-4 text-yellow-500 fill-current" />
                    )}
                    {getEnvironmentBadge(gateway.environment)}
                    <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                      {gateway.type}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-medium">Provider:</span> {gateway.provider}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-medium">Key ID:</span> {gateway.configuration.keyId}
                      </p>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                          Key Secret:
                        </span>
                        {showSensitiveData[gateway._id] ? (
                          <span className="text-sm text-gray-900 dark:text-gray-100 font-mono">
                            {gateway.configuration.keySecret}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">••••••••••••</span>
                        )}
                        <button
                          onClick={() => toggleSensitiveData(gateway._id)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          {showSensitiveData[gateway._id] ? 
                            <EyeOff className="h-4 w-4" /> : 
                            <Eye className="h-4 w-4" />
                          }
                        </button>
                        {showSensitiveData[gateway._id] && (
                          <button
                            onClick={() => copyToClipboard(gateway.configuration.keySecret)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      {gateway.configuration.merchantName && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          <span className="font-medium">Store Name:</span> {gateway.configuration.merchantName}
                        </p>
                      )}
                    </div>
                    
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-medium">Supported Methods:</span> {gateway.supportedMethods.join(', ')}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        <span className="font-medium">Transaction Limits:</span> ₹{gateway.limits.minTransaction} - ₹{gateway.limits.maxTransaction.toLocaleString()}
                      </p>
                      {gateway.lastTested && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          <span className="font-medium">Last Tested:</span> {new Date(gateway.lastTested).toLocaleDateString()}
                          {gateway.testResults && (
                            <span className={`ml-2 ${gateway.testResults.success ? 'text-green-600' : 'text-red-600'}`}>
                              {gateway.testResults.success ? '✓' : '✗'}
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons Container - Responsive Layout */}
                <div className="flex items-center ml-2 sm:ml-4">
                  {/* Status Icon */}
                  <div className="hidden sm:block mr-2">
                    {getStatusIcon(gateway.status, gateway.isActive)}
                  </div>
                  
                  {/* Button Grid - 2 rows on mobile, single row on desktop */}
                  <div className="grid grid-cols-3 sm:flex sm:items-center gap-1 sm:gap-2 sm:space-x-0">
                    {/* Row 1 - Main Actions */}
                    <button
                      onClick={() => toggleGatewayStatus(gateway._id)}
                      className={`p-1.5 sm:p-2 rounded-lg transition-colors ${
                        gateway.isActive 
                          ? 'text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20' 
                          : 'text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                      title={gateway.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {gateway.isActive ? <Power className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> : <PowerOff className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
                    </button>

                    <button
                      onClick={() => testGateway(gateway._id)}
                      disabled={testingGateway === gateway._id}
                      className="p-1.5 sm:p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg disabled:opacity-50 transition-colors"
                      title="Test connection"
                    >
                      <TestTube className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    </button>

                    <button
                      onClick={() => handleEdit(gateway)}
                      className="p-1.5 sm:p-2 text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    </button>

                    {/* Second Row Items */}
                    {!gateway.isDefault ? (
                      <button
                        onClick={() => setAsDefault(gateway._id)}
                        className="p-1.5 sm:p-2 text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-lg transition-colors"
                        title="Set as default"
                      >
                        <StarOff className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </button>
                    ) : (
                      <div className="p-1.5 sm:p-2 opacity-50">
                        <Star className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-yellow-500 fill-current" />
                      </div>
                    )}

                    <button
                      onClick={() => deleteGateway(gateway._id, gateway.displayName)}
                      className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg disabled:opacity-50 transition-colors"
                      title="Delete"
                      disabled={gateway.isActive}
                    >
                      <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    </button>

                    {/* Status Icon for Mobile */}
                    <div className="sm:hidden flex items-center justify-center p-1.5">
                      {getStatusIcon(gateway.status, gateway.isActive)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal for Add/Edit Gateway */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-secondary-800 rounded-xl p-4 sm:p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100">
                {editingGateway ? 'Edit Payment Gateway' : 'Add Payment Gateway'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingGateway(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600 p-2 -m-2"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Gateway Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-3 sm:py-2 border border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 rounded-lg sm:rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px] text-base sm:text-sm"
                    placeholder="e.g., razorpay-production"
                    required
                    disabled={!!editingGateway}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Display Name *
                  </label>
                  <input
                    type="text"
                    value={formData.displayName}
                    onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                    className="w-full px-3 py-3 sm:py-2 border border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 rounded-lg sm:rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px] text-base sm:text-sm"
                    placeholder="e.g., Razorpay Payment Gateway"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Provider *
                  </label>
                  <input
                    type="text"
                    value="Razorpay"
                    disabled
                    className="w-full px-3 py-3 sm:py-2 border border-gray-300 dark:border-secondary-600 bg-gray-100 dark:bg-secondary-600 text-gray-600 dark:text-gray-400 rounded-lg sm:rounded-md cursor-not-allowed min-h-[48px] text-base sm:text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">Only Razorpay is supported in this version</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Environment *
                  </label>
                  <select
                    value={formData.environment}
                    onChange={(e) => setFormData(prev => ({ ...prev, environment: e.target.value as any }))}
                    className="w-full px-3 py-3 sm:py-2 border border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 rounded-lg sm:rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[48px] text-base sm:text-sm"
                    required
                  >
                    <option value="SANDBOX">Sandbox (for testing)</option>
                    <option value="PRODUCTION">Production (live payments)</option>
                  </select>
                </div>
              </div>

              {/* Razorpay Configuration */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                  Razorpay API Configuration
                </h3>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <AlertTriangle className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                        How to get Razorpay API Keys:
                      </h4>
                      <ol className="mt-2 text-sm text-blue-700 dark:text-blue-300 list-decimal list-inside space-y-1">
                        <li>Sign up at <a href="https://razorpay.com" target="_blank" rel="noopener noreferrer" className="underline">razorpay.com</a></li>
                        <li>Complete KYC verification</li>
                        <li>Go to Settings → API Keys</li>
                        <li>Generate or view your Key ID and Key Secret</li>
                        <li>For testing, use sandbox keys first</li>
                      </ol>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Razorpay Key ID *
                    </label>
                    <input
                      type="text"
                      value={formData.configuration.keyId}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        configuration: { ...prev.configuration, keyId: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="rzp_test_1234567890 or rzp_live_1234567890"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">This is your public key and can be safely shared</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Razorpay Key Secret *
                    </label>
                    <input
                      type="password"
                      value={formData.configuration.keySecret}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        configuration: { ...prev.configuration, keySecret: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Enter your secret key"
                      required
                    />
                    <p className="text-xs text-red-500 mt-1">⚠️ Keep this secret - never share publicly</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Store/Company Name
                    </label>
                    <input
                      type="text"
                      value={formData.configuration.merchantName || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        configuration: { ...prev.configuration, merchantName: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="E-Nest India"
                    />
                    <p className="text-xs text-gray-500 mt-1">Will be displayed on checkout page</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Theme Color
                    </label>
                    <input
                      type="color"
                      value={formData.configuration.themeColor || '#3B82F6'}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        configuration: { ...prev.configuration, themeColor: e.target.value }
                      }))}
                      className="w-20 h-10 border border-gray-300 dark:border-secondary-600 rounded-md cursor-pointer"
                    />
                    <p className="text-xs text-gray-500 mt-1">Checkout page theme color</p>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Webhook Secret (Optional)
                    </label>
                    <input
                      type="password"
                      value={formData.configuration.webhookSecret || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        configuration: { ...prev.configuration, webhookSecret: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="For webhook verification (optional)"
                    />
                    <p className="text-xs text-gray-500 mt-1">Used to verify webhook authenticity (recommended for production)</p>
                  </div>
                </div>
              </div>

              {/* Transaction Limits */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                  Transaction Limits
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Min Transaction (₹)
                    </label>
                    <input
                      type="number"
                      value={formData.limits.minTransaction}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        limits: { ...prev.limits, minTransaction: Number(e.target.value) }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      min="1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Max Transaction (₹)
                    </label>
                    <input
                      type="number"
                      value={formData.limits.maxTransaction}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        limits: { ...prev.limits, maxTransaction: Number(e.target.value) }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      min="1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Daily Limit (₹)
                    </label>
                    <input
                      type="number"
                      value={formData.limits.dailyLimit}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        limits: { ...prev.limits, dailyLimit: Number(e.target.value) }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      min="1"
                    />
                  </div>
                </div>
              </div>

              {/* Metadata */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
                  Additional Information
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.metadata.description || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        metadata: { ...prev.metadata, description: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      rows={3}
                      placeholder="Description of this payment gateway configuration"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Processing Time
                    </label>
                    <input
                      type="text"
                      value={formData.metadata.processingTime || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        metadata: { ...prev.metadata, processingTime: e.target.value }
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="e.g., Instant, 2-3 business days"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingGateway(null);
                    resetForm();
                  }}
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 text-gray-600 border border-gray-300 rounded-xl sm:rounded-md hover:bg-gray-50 min-h-[48px] font-semibold text-base sm:text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-primary-600 text-white rounded-xl sm:rounded-md hover:bg-primary-700 flex items-center justify-center space-x-2 min-h-[48px] font-semibold text-base sm:text-sm transition-all active:transform active:scale-[0.98]"
                >
                  <Save className="h-5 w-5 sm:h-4 sm:w-4" />
                  <span>{editingGateway ? 'Update Gateway' : 'Create Gateway'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentGatewayManagement;